// §4.6 Lot Detail (office) — lot lifecycle + the trace launcher.
//
// Header: lot_code · qc_status pill · origin chip (supplier + supplier_lot_code,
// or the producing work order). Then the attributes grid, the stock summary by
// location, the units table, the qc Quarantine/Release actions (reason
// required), and the Trace ↑ upstream / Trace ↓ downstream launchers into §4.7.
//
// Two honest edges:
//   * `units[]` carries at most 200 (§5 row 8); when `units_truncated` is true
//     the table says so with the real total instead of quietly showing a slice.
//   * `work_order_id` is Module 04's — while 04 is unbound the chip shows the
//     reference without pretending to link somewhere.

import { enumLabel } from "../../../../lib/enum-label";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { DataTable } from "../../../../components/data-table";
import { CardSkeleton, EmptyState, ErrorState } from "../../../../components/states";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { useTerm } from "../../../onboarding/hooks/terminology";
import { Button } from "../../../../components/button";
// The register's labelled-control Field, aliased: this area's own `Field` is
// the dt/dd detail row from inventory-ui.
import { Field as FormField } from "../../../../components/field";
import { AdjustStockLink } from "../components/adjust-stock-link";
import { Field, InventoryStatus, LocationPath } from "../components/inventory-ui";
import {
  useLocationPathIndex,
  useLot,
  useWorkOrderRef,
  useProductIndex,
  useQuarantineLot,
  useReleaseLot,
} from "../hooks/inventory-queries";
import { useInventoryPermissions } from "../hooks/permissions";
import { useProductionPermissions } from "../../production/hooks/permissions";
import { isAdjustablePlace, placeEntrySearch } from "../lib/adjust";
import { inventoryErrorMessage } from "../lib/errors";
import { formatBusinessDate, shortId, formatMeasureWithUom } from "../lib/measure";

/** Lot attributes are `unknown` on the wire (§2.2 jsonb) — render every shape
 * without ever producing "[object Object]". */
function renderAttribute(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  // JSON.stringify returns undefined only for undefined/function/symbol, all
  // already handled above — so the cast is safe and the result is always text.
  return JSON.stringify(value);
}

export function LotDetailScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { term } = useTerm();
  const perms = useInventoryPermissions();
  const params = useParams({ strict: false });
  const id = typeof params.id === "string" ? params.id : "";

  const detail = useLot(id);
  // Module 04's work-order read and page both need production.read; a lot
  // reader without it gets the reference as text and no read is made.
  const canReadWorkOrder = useProductionPermissions().has("production.read");
  const workOrderRef = useWorkOrderRef(
    canReadWorkOrder ? (detail.data?.lot.work_order_id ?? "") : "",
  );
  const quarantine = useQuarantineLot();
  const release = useReleaseLot();
  const [action, setAction] = useState<"quarantine" | "release" | null>(null);
  const [reason, setReason] = useState("");

  const products = useProductIndex(detail.data !== undefined ? [detail.data.lot.product_id] : []);
  // §4.12: the Adjust entry shows only on a place that holds stock — the lot's
  // work in progress (the production location) is booked AGAINST, never into.
  const summaryPlaces = useLocationPathIndex(
    (detail.data?.stock_summary ?? []).map((row) => row.location_id),
  );

  useEffect(() => {
    if (detail.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [detail.error, navigate]);

  if (detail.isPending || detail.error instanceof UnauthorizedError) {
    return <CardSkeleton lines={6} label={t("inventory.lotDetail.loading")} />;
  }
  if (detail.error) {
    return (
      <ErrorState
        title={t("inventory.lotDetail.errorTitle")}
        detail={detail.error.message}
        retryLabel={t("inventory.common.retry")}
        onRetry={() => void detail.refetch()}
        requestId={requestIdOf(detail.error)}
        requestIdLabel={t("inventory.common.requestId")}
      />
    );
  }

  const { lot, stock_summary: summary, units, units_total: unitsTotal } = detail.data;
  const product = products.byId.get(lot.product_id);
  const canQc = perms.has("inventory.quarantine");
  const canTrace = perms.has("inventory.trace.read");
  const attributes = Object.entries(lot.attributes);

  return (
    <section className="lot-detail">
      <PageHeader
        title={
          <>
            {term("lot")} {lot.lot_code}
          </>
        }
        actions={
          <>
            <InventoryStatus status={lot.qc_status} scope="qc" />
            <AdjustStockLink
              search={{ product_id: lot.product_id, lot_id: lot.id, from: "lot" }}
              label={t("inventory.lotDetail.adjust")}
              testId="lot-adjust"
            />
          </>
        }
      />

      <dl className="inv-fields">
        <Field label={t("inventory.lotDetail.product")}>
          <Link to="/catalogue/products/$id" params={{ id: lot.product_id }}>
            {product === undefined ? shortId(lot.product_id) : `${product.sku} · ${product.name}`}
          </Link>
        </Field>
        <Field label={t("inventory.lotDetail.origin")}>
          <span className="origin-chip">{enumLabel(t, "inventory.vocab.origin", lot.origin)}</span>{" "}
          {lot.supplier_id !== null ? (
            <span className="origin-detail">
              {t("inventory.lotDetail.supplier", { id: shortId(lot.supplier_id) })}
              {lot.supplier_lot_code !== null ? ` · ${lot.supplier_lot_code}` : ""}
            </span>
          ) : null}
          {lot.work_order_id !== null ? (
            <span className="origin-detail">
              {/* The work order that made the lot, by its number, linked to
                  module 04's page. It used to print a short id beside a line
                  saying production was not counted, which stopped being true
                  when production went live (D-425). */}
              {canReadWorkOrder ? (
                <Link
                  to="/production/work-orders/$id"
                  params={{ id: lot.work_order_id }}
                  className="ds-lnk"
                  data-testid="lot-work-order"
                >
                  {`${term("work_order")} ${workOrderRef.data?.woNo ?? shortId(lot.work_order_id)}`}
                </Link>
              ) : (
                <span data-testid="lot-work-order">
                  {`${term("work_order")} ${shortId(lot.work_order_id)}`}
                </span>
              )}
            </span>
          ) : null}
        </Field>
        <Field label={t("inventory.lotDetail.received")}>
          {lot.received_on === null
            ? t("inventory.common.none")
            : formatBusinessDate(lot.received_on, i18n.language)}
        </Field>
        <Field label={t("inventory.lotDetail.expires")}>
          {lot.expires_on === null
            ? t("inventory.common.none")
            : formatBusinessDate(lot.expires_on, i18n.language)}
        </Field>
      </dl>

      <div className="unit-actions" role="group" aria-label={t("inventory.lotDetail.actions")}>
        {canQc ? (
          lot.qc_status === "quarantined" ? (
            <Button
              onClick={() => {
                setAction("release");
                setReason("");
              }}
            >
              {t("inventory.lotDetail.release")}
            </Button>
          ) : (
            <Button
              onClick={() => {
                setAction("quarantine");
                setReason("");
              }}
            >
              {t("inventory.lotDetail.quarantine")}
            </Button>
          )
        ) : null}
        {canTrace ? (
          <>
            <Link to="/inventory/lots/$id/trace" params={{ id }} hash="up" className="ds-lnk">
              {t("inventory.lotDetail.traceUp")}
            </Link>
            <Link to="/inventory/lots/$id/trace" params={{ id }} hash="down" className="ds-lnk">
              {t("inventory.lotDetail.traceDown")}
            </Link>
          </>
        ) : null}
      </div>

      {quarantine.isSuccess || release.isSuccess ? (
        <p className="inv-success" role="status">
          {t("inventory.lotDetail.qcDone")}
        </p>
      ) : null}

      {action !== null ? (
        <form
          className="inv-panel"
          onSubmit={(event) => {
            event.preventDefault();
            // The form CLOSES on success (the outcome is announced above the
            // action bar): keeping a live submit after a settled qc transition
            // meant a second click was a guaranteed LOT_STATE_INVALID.
            const settle = {
              onSuccess: () => {
                setAction(null);
              },
            };
            if (action === "quarantine") {
              quarantine.mutate({ id, reason: reason.trim() }, settle);
            } else {
              release.mutate(
                { id, ...(reason.trim().length > 0 ? { note: reason.trim() } : {}) },
                settle,
              );
            }
          }}
        >
          <FormField
            label={
              action === "quarantine"
                ? t("inventory.lotDetail.quarantineReason")
                : t("inventory.lotDetail.releaseNote")
            }
          >
            <textarea
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
              }}
              required={action === "quarantine"}
            />
          </FormField>
          {quarantine.isError || release.isError ? (
            <p className="form-error" role="alert">
              {inventoryErrorMessage(
                t,
                quarantine.error ?? release.error,
                "inventory.lotDetail.qcFailed",
              )}
            </p>
          ) : null}
          <div className="form-actions">
            <Button
              type="submit"
              variant="primary"
              busy={quarantine.isPending || release.isPending}
              disabled={action === "quarantine" && reason.trim().length === 0}
            >
              {t("inventory.common.confirm")}
            </Button>
            <Button
              onClick={() => {
                setAction(null);
              }}
            >
              {t("inventory.common.cancel")}
            </Button>
          </div>
        </form>
      ) : null}

      <h2>{t("inventory.lotDetail.attributes")}</h2>
      {attributes.length === 0 ? (
        <p className="inv-hint">{t("inventory.lotDetail.noAttributes")}</p>
      ) : (
        <dl className="attr-grid">
          {attributes.map(([key, value]) => (
            <div className="attr-cell" key={key}>
              <dt>{key}</dt>
              <dd>{renderAttribute(value)}</dd>
            </div>
          ))}
        </dl>
      )}

      <h2>{t("inventory.lotDetail.stockSummary")}</h2>
      {summary.length === 0 ? (
        <EmptyState
          title={t("inventory.lotDetail.noStockTitle")}
          body={t("inventory.lotDetail.noStockBody")}
        />
      ) : (
        <DataTable
          columns={[
            {
              key: "location_path",
              header: term("location"),
              id: true,
              render: (row) => <LocationPath path={row.location_path} />,
            },
            {
              key: "on_hand",
              header: t("inventory.lotDetail.colOnHand"),
              numeric: true,
              render: (row) =>
                formatMeasureWithUom(row.on_hand, row.uom, { locale: i18n.language }),
            },
            // §4.12 (D-315): the form opens on this batch at this place. The
            // row sums containers and loose stock; the form states which of the
            // two a bulk adjustment can reach.
            ...(perms.has("inventory.adjust")
              ? [
                  {
                    key: "adjust",
                    header: t("inventory.common.actions"),
                    actions: true,
                    render: (row: (typeof summary)[number]) =>
                      !isAdjustablePlace(summaryPlaces.byId.get(row.location_id)?.kind) ? null : (
                        <AdjustStockLink
                          search={{
                            product_id: lot.product_id,
                            lot_id: lot.id,
                            location_id: row.location_id,
                            ...placeEntrySearch(summaryPlaces.byId.get(row.location_id)?.kind),
                            from: "lot",
                          }}
                          label={t("inventory.lotDetail.adjustRow")}
                          testId={`lot-stock-adjust-${row.location_id}`}
                        />
                      ),
                  },
                ]
              : []),
          ]}
          rows={summary}
          rowKey={(row) => row.location_id}
          empty={null}
        />
      )}

      <h2>{term("stock_unit")}</h2>
      {units.length === 0 ? (
        <p className="inv-hint">{t("inventory.lotDetail.noUnits")}</p>
      ) : (
        <>
          {detail.data.units_truncated ? (
            <p className="degraded-note" role="note">
              {t("inventory.lotDetail.unitsTruncated", { shown: units.length, total: unitsTotal })}
            </p>
          ) : null}
          <DataTable
            columns={[
              {
                key: "serial_code",
                header: t("inventory.lotDetail.colSerial"),
                id: true,
                render: (unit) => (
                  <Link to="/inventory/units/$id" params={{ id: unit.id }} className="ds-lnk">
                    {unit.serial_code}
                  </Link>
                ),
              },
              {
                key: "status",
                header: t("inventory.lotDetail.colStatus"),
                render: (unit) => <InventoryStatus status={unit.status} scope="status" />,
              },
              {
                key: "remaining_measure",
                header: t("inventory.lotDetail.colRemaining"),
                numeric: true,
                render: (unit) =>
                  formatMeasureWithUom(unit.remaining_measure, unit.measure_uom, {
                    locale: i18n.language,
                  }),
              },
              {
                key: "location_path",
                header: term("location"),
                render: (unit) => <LocationPath path={unit.location_path} />,
              },
            ]}
            rows={units}
            rowKey={(unit) => unit.id}
            empty={null}
          />
        </>
      )}
    </section>
  );
}
